/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { CKEditor5, EditorType } from '@ckeditor/ckeditor5-metadata';
import { isEmpty } from 'lodash';

import { UsersIntegration } from '../../components/CKEditor5Preview/CKEditor5/UsersIntegration';
import { CommentsIntegration } from '../../components/CKEditor5Preview/CKEditor5/CommentsIntegration';
import { TrackChangesIntegration } from '../../components/CKEditor5Preview/CKEditor5/TrackChangesIntegration';
import { RevisionHistoryIntegration } from '../../components/CKEditor5Preview/CKEditor5/RevisionHistoryIntegration';
import type { EditorLanguage } from '../../components/LanguageSelection/languages';
import type { EditorPlugins } from '../../types/EditorPlugins';
import { getPluginConstructorsByNames } from '../getPluginConstructorsByNames/getPluginConstructorsByNames';
import { requiresCommentsContainer } from '../requiresCommentsContainer/requiresCommentsContainer';
import { requiresDocumentOutlineContainer } from '../requiresDocumentOutlineContainer/requiresDocumentOutlineContainer';
import { requiresMinimapContainer } from '../requiresMinimapContainer/requiresMinimapContainer';
import { requiresPaginationPlugin } from '../requiresPaginationPlugin/requiresPaginationPlugin';
import {
	requiresRevisionHistoryContainers
} from '../requiresRevisionHistoryContainers/requiresRevisionHistoryContainers';
import { requiresUsersIntegrationPlugin } from '../requiresUsersIntegrationPlugin/requiresUsersIntegrationPlugin';
import {
	requiresCloudServicesConfiguration
} from '../requiresCloudServicesConfiguration/requiresCloudServicesConfiguration';
import type { ToolbarOptions } from '../../types/ToolbarsOptions';
import {
	getBalloonToolbar,
	getBlockToolbar,
	getImageToolbar,
	getMainToolbar,
	getTableToolbar
} from '../getToolbar/getToolbar';
import { CKEditor5ArticleImageRightIcon } from '../../assets';
import { isDecoupledEditor } from '../isDecoupledEditor/isDecoupledEditor';
import {
	requiresCollaborativeFeatureIntegrationPlugin
} from '../requiresCollaborativeFeatureIntegrationPlugin/requiresCollaborativeFeatureIntegrationPlugin';

export type EditorConfigOptions = {
	toolbarOptions: ToolbarOptions;
	editorPlugins: EditorPlugins;
	editorLanguage: EditorLanguage;
	editorType: EditorType;
	licenseKey?: string;
	aiAuthorizationToken?: string;
	aiApiUrl?: string;
	collaborationChannelId?: string;
	cloudServicesTokenUrl?: string;
	cloudServicesWebSocketUrl?: string;
};

type EditorRefs = {
	// The `PresenceList` is mocked in the preview mode. Hence, we do not need a container.
	// See: https://github.com/ckeditor/ckeditor5-builder/blob/master/adr/0005-mock-rtc-features-in-preview.md.
	presenceList: HTMLElement | null;
	outlineSidebar: HTMLElement;
	annotationsSidebar: HTMLElement;
	editorContainer: HTMLElement;
	minimapSidebar: HTMLElement;
	revisionHistoryContainer: HTMLElement;
	revisionHistoryEditor: HTMLElement;
	revisionHistorySidebar: HTMLElement;
};

type EditorConfigCustomizer = {
	pluginsToConstructors?: ( plugins: EditorPlugins ) => Array<CKEditor5.PluginConstructor> | EditorPlugins;
};

const COMMENTS_EDITOR_PLUGINS: EditorPlugins = [
	'Autoformat', 'Bold', 'Italic', 'List', 'Mention'
].sort( sortAlphabetically );

export function prepareEditorConfig(
	options: EditorConfigOptions,
	refs: EditorRefs,
	{ pluginsToConstructors = getPluginConstructorsByNames }: EditorConfigCustomizer = {}
): CKEditor5.EditorConfig {
	const editorPlugins = options.editorPlugins.sort( sortAlphabetically );
	const isEmailSetup = editorPlugins.includes( 'EmailConfigurationHelper' );

	const config: CKEditor5.EditorConfig = {
		toolbar: {
			items: getMainToolbar( {
				editorPlugins,
				isMenuBarOn: options.toolbarOptions.isMenuBarUsed,
				essentialOnly: options.toolbarOptions.buttonsOptions === 'essential'
			} ),
			shouldNotGroupWhenFull: !options.toolbarOptions.isCollapsable
		},
		language: options.editorLanguage,
		placeholder: 'Type or paste your content here!',
		plugins: pluginsToConstructors( editorPlugins )
	};

	const extraPlugins: Array<CKEditor5.PluginConstructor<CKEditor5.Editor>> = [];

	if ( options.toolbarOptions.isBlockToolbarOn ) {
		config.blockToolbar = getBlockToolbar( { editorPlugins } );
	}

	if ( options.toolbarOptions.isBalloonToolbarOn ) {
		config.balloonToolbar = getBalloonToolbar( { editorPlugins } );
	}

	if ( editorPlugins.includes( 'TableToolbar' ) ) {
		config.table = {
			contentToolbar: getTableToolbar( { editorPlugins } )
		};
	}

	if ( options.toolbarOptions.isMenuBarUsed && !isDecoupledEditor( options.editorType ) ) {
		config.menuBar = {
			isVisible: true
		};
	}

	if ( options.licenseKey ) {
		config.licenseKey = options.licenseKey;
	}

	if ( requiresDocumentOutlineContainer( editorPlugins ) ) {
		config.documentOutline = {
			container: refs.outlineSidebar
		};
	}

	if ( requiresCommentsContainer( editorPlugins ) ) {
		const commentsEditorPlugins = COMMENTS_EDITOR_PLUGINS.filter( plugin => editorPlugins.includes( plugin ) );

		config.comments = {
			editorConfig: {
				// `comments#editorConfig` does not accept strings.
				extraPlugins: pluginsToConstructors( commentsEditorPlugins ) as Array<CKEditor5.PluginConstructor>
			}
		};

		config.sidebar = {
			container: refs.annotationsSidebar
		};
	}

	if ( requiresMinimapContainer( editorPlugins ) ) {
		config.minimap = {
			container: refs.minimapSidebar,
			extraClasses: 'editor-container_include-minimap ck-minimap__iframe-content'
		};
	}

	if ( requiresRevisionHistoryContainers( editorPlugins ) ) {
		config.revisionHistory = {
			editorContainer: refs.editorContainer,
			viewerContainer: refs.revisionHistoryContainer,
			viewerEditorElement: refs.revisionHistoryEditor,
			viewerSidebarContainer: refs.revisionHistorySidebar,
			resumeUnsavedRevision: true
		};
	}

	if ( requiresPaginationPlugin( editorPlugins ) ) {
		config.pagination = {
			pageWidth: '21cm',
			pageHeight: '29.7cm',
			pageMargins: {
				top: '20mm',
				bottom: '20mm',
				right: '12mm',
				left: '12mm'
			}
		};
	}

	if ( requiresCloudServicesConfiguration( editorPlugins ) ) {
		config.collaboration = {
			channelId: options.collaborationChannelId
		};
		config.cloudServices = {
			tokenUrl: options.cloudServicesTokenUrl,
			webSocketUrl: options.cloudServicesWebSocketUrl
		};
	}

	if ( editorPlugins.includes( 'OpenAITextAdapter' ) ) {
		config.ai = {
			openAI: {
				requestHeaders: {
					Authorization: options.aiAuthorizationToken!
				}
			}
		};

		if ( options.aiApiUrl ) {
			config.ai.openAI!.apiUrl = options.aiApiUrl;
		}
	}

	if ( editorPlugins.includes( 'PresenceList' ) ) {
		config.presenceList = {
			container: refs.presenceList!
		};
	}

	if ( editorPlugins.includes( 'ListProperties' ) ) {
		config.list = {
			properties: {
				styles: true,
				startIndex: true,
				reversed: isEmailSetup ? false : true
			}
		};
	}

	if ( editorPlugins.includes( 'GeneralHtmlSupport' ) ) {
		config.htmlSupport = {
			allow: [
				{
					name: isEmailSetup ? /^(div|table|tbody|tr|td|span|img|h1|h2|h3|p|a)$/ : /^.*$/,
					styles: true,
					attributes: true,
					classes: true
				}
			]
		};
	}

	if ( editorPlugins.includes( 'ExportPdf' ) ) {
		config.exportPdf = {
			stylesheets: [],
			fileName: 'export-pdf-demo.pdf',
			converterOptions: {
				format: isDecoupledEditor( options.editorType ) ? 'A4' : 'Tabloid',
				margin_top: isDecoupledEditor( options.editorType ) ? '20mm' : '20mm',
				margin_bottom: isDecoupledEditor( options.editorType ) ? '20mm' : '20mm',
				margin_right: isDecoupledEditor( options.editorType ) ? '12mm' : '24mm',
				margin_left: isDecoupledEditor( options.editorType ) ? '12mm' : '24mm',
				page_orientation: 'portrait'
			}
		};
	}

	if ( editorPlugins.includes( 'ExportWord' ) ) {
		config.exportWord = {
			stylesheets: [],
			fileName: 'export-word-demo.docx',
			converterOptions: {
				document: {
					orientation: 'portrait',
					size: isDecoupledEditor( options.editorType ) ? 'A4' : 'Tabloid',
					margins: {
						top: isDecoupledEditor( options.editorType ) ? '20mm' : '20mm',
						bottom: isDecoupledEditor( options.editorType ) ? '20mm' : '20mm',
						right: isDecoupledEditor( options.editorType ) ? '12mm' : '24mm',
						left: isDecoupledEditor( options.editorType ) ? '12mm' : '24mm'
					}
				}
			}
		};
	}

	if ( editorPlugins.includes( 'ExportInlineStyles' ) ) {
		config.exportInlineStyles = {
			stylesheets: []
		};
	}

	if ( editorPlugins.includes( 'Link' ) ) {
		config.link = {
			addTargetToExternalLinks: true,
			defaultProtocol: 'https://',
			decorators: {
				toggleDownloadable: {
					mode: 'manual',
					label: 'Downloadable',
					attributes: {
						download: 'file'
					}
				}
			}
		};
	}

	if ( editorPlugins.includes( 'Style' ) ) {
		config.style = {
			definitions: [
				{
					name: 'Article category',
					element: 'h3',
					classes: [ 'category' ]
				},
				{
					name: 'Title',
					element: 'h2',
					classes: [ 'document-title' ]
				},
				{
					name: 'Subtitle',
					element: 'h3',
					classes: [ 'document-subtitle' ]
				},
				{
					name: 'Info box',
					element: 'p',
					classes: [ 'info-box' ]
				},
				{
					name: 'CTA Link Primary',
					element: 'a',
					classes: [ 'button', 'button--green' ]
				},
				{
					name: 'CTA Link Secondary',
					element: 'a',
					classes: [ 'button', 'button--black' ]
				},
				{
					name: 'Marker',
					element: 'span',
					classes: [ 'marker' ]
				},
				{
					name: 'Spoiler',
					element: 'span',
					classes: [ 'spoiler' ]
				}
			]
		};
	}

	if ( editorPlugins.includes( 'FontSize' ) ) {
		config.fontSize = {
			options: [ 10, 12, 14, 'default', 18, 20, 22 ],
			supportAllValues: true
		};
	}

	if ( editorPlugins.includes( 'FontFamily' ) ) {
		config.fontFamily = { supportAllValues: true };
	}

	if ( editorPlugins.includes( 'ImageBlock' ) || editorPlugins.includes( 'ImageInline' ) ) {
		const imageConfig: CKEditor5.EditorConfig[ 'image' ] = {};

		if ( editorPlugins.includes( 'ImageToolbar' ) ) {
			imageConfig.toolbar = getImageToolbar( { editorPlugins } );
		}

		if ( editorPlugins.includes( 'ImageStyle' ) ) {
			const imageStyles = getImageStyles( editorPlugins );

			if ( imageStyles.length ) {
				imageConfig.styles = {
					options: imageStyles
				};
			}
		}

		if ( !isEmpty( imageConfig ) ) {
			config.image = imageConfig;
		}
	}

	if ( editorPlugins.includes( 'Template' ) ) {
		/* eslint-disable max-len, @typescript-eslint/quotes */
		config.template = {
			definitions: [
				{
					title: 'Introduction',
					description: 'Simple introduction to an article',
					icon: CKEditor5ArticleImageRightIcon,
					data: `<h2>Introduction</h2><p>In today's fast-paced world, keeping up with the latest trends and insights is essential for both personal growth and professional development. This article aims to shed light on a topic that resonates with many, providing valuable information and actionable advice. Whether you're seeking to enhance your knowledge, improve your skills, or simply stay informed, our comprehensive analysis offers a deep dive into the subject matter, designed to empower and inspire our readers.</p>`
				}
			]
		};
		/* eslint-enable max-len, @typescript-eslint/quotes */
	}

	if ( editorPlugins.includes( 'Heading' ) ) {
		config.heading = {
			options: [
				{ model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
				{ model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
				{ model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
				{ model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
				{ model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
				{ model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
				{ model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
			]
		};
	}

	if ( requiresUsersIntegrationPlugin( editorPlugins ) ) {
		extraPlugins.push( UsersIntegration );
	}

	if ( requiresCollaborativeFeatureIntegrationPlugin( editorPlugins, 'Comments' ) ) {
		extraPlugins.push( CommentsIntegration );
	}

	if ( requiresCollaborativeFeatureIntegrationPlugin( editorPlugins, 'TrackChanges' ) ) {
		extraPlugins.push( TrackChangesIntegration );
	}

	if ( requiresCollaborativeFeatureIntegrationPlugin( editorPlugins, 'RevisionHistory' ) ) {
		extraPlugins.push( RevisionHistoryIntegration );
	}

	if ( extraPlugins.length ) {
		config.extraPlugins = extraPlugins;
	}

	return config;
}

function getImageStyles( editorPlugins: EditorPlugins ) {
	if ( editorPlugins.includes( 'ImageBlock' ) && editorPlugins.includes( 'ImageInline' ) ) {
		return [];
	}

	return editorPlugins.includes( 'ImageBlock' ) ?
		[ 'alignBlockLeft', 'block', 'alignBlockRight' ] :
		[ 'inline', 'alignLeft', 'alignRight' ];
}

function sortAlphabetically( first: string, second: string ) {
	return first.localeCompare( second );
}
